/**
 * Project Limits Configuration
 * Controls maximum scenes and duration for recording mode projects
 * These limits help manage costs and performance
 */

export const PROJECT_LIMITS = {
  MAX_SCENES_RECORDING_MODE: 30,
  MAX_PROJECT_DURATION_MS: 60 * 60 * 1000, // 1 hour in milliseconds
  MAX_PROJECT_DURATION_MINUTES: 60,
} as const;

/**
 * Check if adding a scene would exceed the limit
 * @param currentSceneCount Current number of scenes
 * @param isBlankMode Whether in blank mode (no limits)
 * @returns Whether adding a scene is allowed
 */
export function canAddScene(currentSceneCount: number, isBlankMode: boolean): boolean {
  if (isBlankMode) return true;
  return currentSceneCount < PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE;
}

/**
 * Get remaining scenes that can be added
 * @param currentSceneCount Current number of scenes
 * @param isBlankMode Whether in blank mode (unlimited)
 * @returns Number of scenes that can still be added, or Infinity for blank mode
 */
export function getRemainingScenes(currentSceneCount: number, isBlankMode: boolean): number {
  if (isBlankMode) return Infinity;
  return Math.max(0, PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE - currentSceneCount);
}

/**
 * Check if project duration exceeds the limit
 * @param totalDurationMs Total project duration in milliseconds
 * @param isBlankMode Whether in blank mode (no limits)
 * @returns Whether the duration exceeds the limit
 */
export function exceedsMaxDuration(totalDurationMs: number, isBlankMode: boolean): boolean {
  if (isBlankMode) return false;
  return totalDurationMs > PROJECT_LIMITS.MAX_PROJECT_DURATION_MS;
}

/**
 * Check if project can be exported
 * @param totalDurationMs Total project duration in milliseconds
 * @param isBlankMode Whether in blank mode (no limits for export, but still validates basic requirements)
 * @returns Object with canExport flag and reason if not allowed
 */
export function canExportProject(
  totalDurationMs: number, 
  isBlankMode: boolean
): { canExport: boolean; reason?: string } {
  if (!isBlankMode && totalDurationMs > PROJECT_LIMITS.MAX_PROJECT_DURATION_MS) {
    const durationMinutes = Math.ceil(totalDurationMs / 60000);
    return {
      canExport: false,
      reason: `Project duration (${durationMinutes} min) exceeds maximum allowed (${PROJECT_LIMITS.MAX_PROJECT_DURATION_MINUTES} min)`,
    };
  }
  return { canExport: true };
}

/**
 * Format duration for display
 * @param durationMs Duration in milliseconds
 * @returns Formatted string like "5:30" or "1:05:30"
 */
export function formatDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
